import React from "react";
import { getManualReadingStatusDetails } from "../../services/dashboard/DashboardService";
import { IManualReadingStatusCount } from "../../vm";
import { ToastContext } from "../common/ToastProvider";
import moment from "moment";
import {
  FontWeights,
  getTheme,
  IconButton,
  IIconProps,
  mergeStyleSets,
  Modal,
  Stack,
  Text,
  Toggle,
} from "office-ui-fabric-react";
import { ResponsiveBar } from "@nivo/bar";
import { getPercentageValue } from "../../services/UtilService";
import { Loading } from "../../services/Loading";

export interface ManualReadingDetailsModalProps {
  startDate: Date | string;
  endDate: Date | string;
  datacenterId: string;
  unit: string;
  handleModalClose: any;
}

export const INFRA_DICT: { [Key: string]: string } = {
  ups: "UPS",
  transformer: "Transformer",
  ht: "HT Panel",
  smps: "SMPS",
  pac: "PAC/PAHU",
  "chiller-plant": "Cooled Chiller",
  idc: "IDC Temperature",
  dg: "DG Set",
};

const ManualReadingDetailsModal: React.FC<ManualReadingDetailsModalProps> = ({
  startDate,
  endDate,
  datacenterId,
  unit,
  handleModalClose,
}) => {
  // custom hooks
  const { showToast } = React.useContext(ToastContext);

  // state
  const [state, setState] = React.useState({
    isLoading: false,
    manualReadings: [],
    searchObj: {
      startDate: moment(startDate).format("YYYY-MM-DD"),
      endDate: moment(endDate).format("YYYY-MM-DD"),
      datacenterId: datacenterId,
      unit: unit,
    },
    graphToggle: false,
  } as {
    isLoading: boolean;
    manualReadings: IManualReadingStatusCount[];
    searchObj: {
      startDate: string;
      endDate: string;
      datacenterId: string;
      unit: string;
    };
    graphToggle: boolean;
  });

  React.useEffect(() => {
    const asyncFunc = async () => {
      await getManualReadingDetails();
    };
    asyncFunc();
  }, []);

  const getManualReadingDetails = async () => {
    setState({ ...state, isLoading: true });

    let result = await getManualReadingStatusDetails(state.searchObj);
    if (result?.success) {
      setState({
        ...state,
        manualReadings: result.data,
        isLoading: false,
      });
    } else {
      setState({ ...state, isLoading: false });
      showToast(
        result?.message || "Error while getting manual readings details",
        "error"
      );
    }
  };

  const hideModal = () => {
    handleModalClose(false);
  };

  const cancelIcon: IIconProps = { iconName: "Cancel" };

  return (
    <React.Fragment>
      {state.isLoading && <Loading />}
      <Modal
        titleAriaId={"title-id"}
        isOpen={true}
        onDismiss={hideModal}
        isBlocking={true}
        containerClassName={contentStyles.container}
      >
        <div className={contentStyles.header}>
          <span id={"title-id"}>{`${INFRA_DICT[unit]} Details View`}</span>
          <IconButton
            styles={iconButtonStyles}
            iconProps={cancelIcon}
            ariaLabel="Close popup modal"
            onClick={hideModal}
          />
        </div>
        <hr />
        <div className={contentStyles.body}>
          <Stack>
            <Stack.Item align="end">
              <Toggle
                className="pointer"
                onText="Grouped Chart"
                offText="Grouped Chart"
                onChange={(e, v) => {
                  setState({
                    ...state,
                    graphToggle: v as boolean,
                  });
                }}
              />
            </Stack.Item>
          </Stack>
          <div className="ms-Grid-row">
            <div className="ms-Grid-col pt-0 ms-sm12 ms-md12 ms-lg12">
              <div
                className={`bar-container ${
                  state.manualReadings.length === 0 ? "no-data" : ""
                }`}
              >
                {state.manualReadings.length > 0 ? (
                  <ResponsiveBar
                    data={state.manualReadings.map((d) => {
                      return {
                        id: d.type,
                        label: d.type,
                        Approved: d.approved,
                        ApprovedColor: "#008000",
                        Submitted: d.submitted,
                        SubmittedColor: "#135c8a",
                        Rejected: d.rejected,
                        RejectedColor: "#d62828",
                        "Not Submitted":
                          d.totalCount - d.approved - d.submitted - d.rejected,
                        "Not SubmittedColor": "#daa520",
                        // "Not In Threshold": getPercentageValue(
                        //   d.notInThreshold,
                        //   d.totalCount
                        // ),
                        // "Not In ThresholdColor": "#FF7F50",
                      };
                    })}
                    keys={[
                      "Not Submitted",
                      "Submitted",
                      "Approved",
                      "Rejected",
                      // "Not In Threshold",
                    ]}
                    groupMode={state.graphToggle ? "grouped" : "stacked"}
                    indexBy="label"
                    margin={{ top: 50, right: 130, bottom: 50, left: 60 }}
                    padding={state.graphToggle === true ? 0.1 : 0.3}
                    colors={({ id, data }) => data[`${id}Color`]}
                    borderColor={{
                      from: "color",
                      modifiers: [["darker", 1.6]],
                    }}
                    axisTop={null}
                    axisRight={null}
                    axisBottom={{
                      tickSize: 10,
                      tickPadding: 10,
                      tickRotation: 0,
                      //   legend: "country",
                      //   legendPosition: "middle",
                      //   legendOffset: 32,
                    }}
                    axisLeft={{
                      tickSize: 10,
                      tickPadding: 15,
                      tickRotation: 0,
                      //   legend: "food",
                      //   legendPosition: "middle",
                      //   legendOffset: -40,
                      format: (v) => (Number.isInteger(v as any) ? `${v}` : ``),
                      // tickValues: [
                      //   "0%",
                      //   "10%",
                      //   "20%",
                      //   "30%",
                      //   "40%",
                      //   "50%",
                      //   "60%",
                      //   "70%",
                      //   "80%",
                      //   "90%",
                      //   "100%",
                      // ],
                      // renderTick: (v) =>
                      //   Number.isInteger(v as any) ? `${v}` : ``,
                    }}
                    labelSkipWidth={12}
                    labelSkipHeight={12}
                    // labelTextColor={{
                    //   from: "color",
                    //   modifiers: [["darker", 1.6]],
                    // }}
                    labelTextColor="#fff"
                    legends={[
                      {
                        dataFrom: "keys",
                        anchor: "top",
                        direction: "row",
                        justify: false,
                        translateX: 20,
                        translateY: -50,
                        itemsSpacing: 5,
                        itemWidth: 100,
                        itemHeight: 20,
                        itemDirection: "left-to-right",
                        itemOpacity: 0.85,
                        symbolSize: 20,
                        effects: [
                          {
                            on: "hover",
                            style: {
                              itemOpacity: 1,
                            },
                          },
                        ],
                      },
                    ]}
                    animate={true}
                    motionStiffness={90}
                    motionDamping={15}
                  />
                ) : (
                  <Text>No Readings Found</Text>
                )}
              </div>
            </div>
          </div>
        </div>
      </Modal>
    </React.Fragment>
  );
};

const theme = getTheme();
const contentStyles = mergeStyleSets({
  container: {
    display: "flex",
    flexFlow: "column nowrap",
    alignItems: "stretch",
    width: 1200,
    height: 550,
  },

  header: [
    theme.fonts.xLarge,
    {
      flex: "1 1 auto",
      borderTop: `4px solid ${theme.palette.orange}`,
      color: theme.palette.neutralPrimary,
      display: "flex",
      alignItems: "center",
      fontWeight: FontWeights.semilight,
      padding: "12px 12px 14px 24px",
    },
  ],
  body: {
    flex: "4 4 auto",
    padding: "0 24px 24px 24px",
    overflowY: "hidden",
  },
});

const iconButtonStyles = {
  root: {
    color: theme.palette.neutralPrimary,
    marginLeft: "auto",
    marginTop: "4px",
    marginRight: "2px",
  },
  rootHovered: {
    color: theme.palette.neutralDark,
  },
};

export default ManualReadingDetailsModal;
